import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import moment from "jalali-moment";
import toast from "react-hot-toast";
import { ComponentItem, TestResultItem } from "./interfaces";
import { fetchTest, fetchTestComponents } from "../../services/test";
import useStore from "../../features/store";
import Loading from "../../components/common/loading";
import TestIntro from "../../components/test/result/testIntro";
import Recommendation from "../../components/test/result/recommendation";
import TestHeader from "../../components/test/testCommon/TestHeader";
import GeneralHealth from "../../components/test/result/generalHealth";
import Survey from "../../components/test/result/survey";
import ChatWithTest from "../../components/test/result/chatWithTest";
import ExclusiveAnalysis from "../../components/test/result/exclusiveAnalysis";
import TestButton from "../../components/test/testCommon/TestButton";
import TestFactors from "../../components/test/result/testFactors";
import apiClient from "../../services/apiClient";

const TestResult = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const step = searchParams.get("step");
  const { fetchBiorhythm }: any = useStore();

  const [loading, setLoading] = useState<boolean>(true);
  const [testInfo, setTestInfo] = useState<any>(null);
  const [testResults, setTestResults] = useState<any>(null);

  const processTestResults = (
    testResult: Record<string, TestResultItem>,
    testComponents: ComponentItem[]
  ) => {
    const mergedData = testComponents
      .filter((component) =>
        Object.values(testResult).some(
          //@ts-ignore
          (item) => item.component_id === component.id
        )
      )
      .map((component) => {
        const testResultItem = Object.values(testResult).find(
          //@ts-ignore
          (item) => item.component_id === component.id
        );

        return testResultItem ? { ...component, ...testResultItem } : null;
      });

    return mergedData;
  };

  function isBeforeTargetDate(dateString: string) {
    const targetDate = moment("1403/12/26", "jYYYY/jM/jD");
    const givenDate = moment(dateString);
    return givenDate.isBefore(targetDate);
  }

  const headerTestItems = [
    {
      step: "1",
      title: "اطلاعات آزمایش",
      info: "اطلاعات کلی درباره شخصی که آزمایش، برای اون تحلیل شده",
    },
    {
      step: "2",
      title: "تحلیل آزمایش",
      info: "بررسی و توضیح وضعیت فاکتورهایی که داخل آزمایش بررسی شدن",
    },
    {
      step: "3",
      title: "تحلیل اختصاصی",
      info: "تحلیل جزئی‌تر آزمایش، امتیاز سلامت و بررسی احتمال بیماری‌های رایج",
    },
    {
      step: "4",
      title: "سلامت عمومی",
      info: "بررسی وضعیت جسمی، سلامت فیزیکی",
    },
    {
      step: "5",
      title: "پیشنهادات",
      info: "نتیجه تحلیل و بررسی هوش مصنوعی بادی‌بان درباره آزمایش",
    },
    {
      step: "6",
      title: "دریافت نتیجه",
      info: "دریافت نتیجه تحلیل آزمایش و نظرسنجی.",
    },
    {
      step: "7",
      title: "بادی‌گپ",
      info: "بادی‌بان کمک می‌کنه با بدنت صحبت کنی",
    },
  ];

  useEffect(() => {
    if (id) {
      fetchTest(id)
        .then((response) => {
          setLoading(false);
          const userInfo = response.data.is_for_other
            ? response.data.temp_user
            : response.data.creator || {};

          const testInfoData = {
            is_old_user: isBeforeTargetDate(response.data.created_at),
            test_id: response.data.id,
            test_date: response.data.test_date,
            files: response.data.files,
            is_pro_analysis: response.data.is_pro_analysis,
            height: response.data.additional_details.height,
            weight: response.data.additional_details.weight,
            bmi: response.data.additional_details.bmi,
            birth_day: userInfo?.birth_day,
            gender: userInfo?.gender,
            name: `${userInfo?.first_name || ""} ${userInfo?.last_name || ""}`,
            allow_to_chat: response.data.allow_to_chat,
          };

          setTestInfo(testInfoData);

          fetchTestComponents().then((res) => {
            if (response.data.components_result) {
              setTestResults({
                results: processTestResults(
                  response.data.components_result,
                  res.data.results
                ),
                ...response.data,
              });
            }
          });

          fetchBiorhythm();
        })
        .catch((err) => {
          if (err?.status === 404) {
            toast.error("تحلیل آزمایش موردنظر، یافت نشد.");
            navigate("/records");
          }
        });
    }
  }, [id]);

  const FooterButtons = () => {
    return (
      <div className="flex flex-col justify-between items-center lg:hidden mt-5 mb-3">
        <div
          className={`flex items-center gap-3 w-full ${
            step === "1"
              ? "justify-end"
              : step === "7"
              ? "justify-start"
              : "justify-between"
          }`}
        >
          {step !== "1" && (
            <TestButton
              title={
                headerTestItems.find(
                  (item: any) => item.step === (Number(step) - 1).toString()
                )?.title
              }
              step={Number(step) - 1}
              isSmall
              isReverse
            />
          )}
          {step !== "7" && (
            <TestButton
              title={
                headerTestItems.find(
                  (item: any) => item.step === (Number(step) + 1).toString()
                )?.title
              }
              step={Number(step) + 1}
              isSmall
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="mt-5 relative flex-1 flex flex-col">
      {!testResults ? (
        <div className="mt-10 mx-auto w-full flex items-center justify-center">
          <Loading type="test-result" />
        </div>
      ) : (
        <>
          {testResults.results.length > 0 && (
            <TestHeader
              headerTestItems={headerTestItems}
              testInfo={testInfo}
              footerButtons={<FooterButtons />}
            />
          )}

          <div className="relative z-1 flex-1 flex flex-col py-3 lg:py-10">
            {step == "1" ? (
              <TestIntro testInfo={testInfo} testResults={testResults} />
            ) : step == "2" ? (
              <TestFactors
                testInfo={testInfo}
                testResults={testResults}
                footerButtons={<FooterButtons />}
              />
            ) : step == "3" ? (
              <ExclusiveAnalysis
                testInfo={testInfo}
                testResults={testResults}
                footerButtons={<FooterButtons />}
              />
            ) : step == "4" ? (
              <GeneralHealth
                testInfo={testInfo}
                testResults={testResults}
                footerButtons={<FooterButtons />}
              />
            ) : step == "5" ? (
              <Recommendation
                testInfo={testInfo}
                testResults={testResults}
                footerButtons={<FooterButtons />}
              />
            ) : step == "6" ? (
              <Survey
                testInfo={testInfo}
                testResults={testResults}
                footerButtons={<FooterButtons />}
              />
            ) : step == "7" ? (
              <ChatWithTest
                testInfo={testInfo}
                footerButtons={<FooterButtons />}
              />
            ) : (
              ""
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TestResult;
